import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { adminService } from '../../services/adminService';
import '../../styles/admin.css';

const formatPrice = (price) => {
  return new Intl.NumberFormat('fr-MA', {
    style: 'currency',
    currency: 'MAD'
  }).format(price || 0);
};

const formatDate = (createdAt) => {
  const date = createdAt?.toDate ? createdAt.toDate() : (createdAt ? new Date(createdAt) : null);
  if (!date) return '—';
  return date.toLocaleString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const data = await adminService.getOrders();
      setOrders(data);
    } catch (error) {
      console.error('Erreur lors du chargement des commandes :', error);
      toast.error('Impossible de charger les commandes');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrder = async (order) => {
    const confirmed = window.confirm(`Supprimer la commande de ${order.customerName} ? Cette action est irréversible.`);
    if (!confirmed) return;

    try {
      await adminService.deleteOrder(order.id);
      setOrders((prev) => prev.filter((o) => o.id !== order.id));
      if (selectedOrder?.id === order.id) setSelectedOrder(null);
      toast.success('Commande supprimée');
    } catch (error) {
      console.error('Erreur lors de la suppression de la commande :', error);
      toast.error('Impossible de supprimer la commande');
    }
  };

  return (
    <div className="admin-products">
      <div className="container">
        <div className="admin-header">
          <h1>
            <i className="fas fa-list-alt"></i>
            Commandes
          </h1>
          <div className="breadcrumb">
            <Link to="/admin/dashboard">
              <i className="fas fa-tachometer-alt"></i> Tableau de bord
            </Link>
            <span>/</span> Commandes
          </div>
        </div>

        <div className="products-table-container">
          <h2>
            <i className="fas fa-shopping-cart"></i> Toutes les commandes ({orders.length})
          </h2>

          <div className="table-responsive">
            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"><div className="spinner"></div></div>
                <p>Chargement des commandes...</p>
              </div>
            ) : orders.length === 0 ? (
              <div className="no-products">
                <i className="fas fa-inbox"></i>
                <h3>Aucune commande pour le moment</h3>
                <p>Les commandes passées par les clients apparaîtront ici.</p>
              </div>
            ) : (
              <table className="products-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Client</th>
                    <th>Téléphone</th>
                    <th>Produits</th>
                    <th>Code promo</th>
                    <th>Total</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id}>
                      <td>{formatDate(order.createdAt)}</td>
                      <td>{order.customerName}</td>
                      <td>{order.phone}</td>
                      <td>{order.totalItems} article{order.totalItems > 1 ? 's' : ''}</td>
                      <td>{order.promoCode || 'Aucun'}</td>
                      <td>{formatPrice(order.totalPrice)}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => setSelectedOrder(order)}
                          >
                            <i className="fas fa-eye"></i> Voir détails
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteOrder(order)}
                            title="Supprimer la commande"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {selectedOrder && (
        <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedOrder(null)}>
              <i className="fas fa-times"></i>
            </button>
            <div className="modal-header">
              <h2>
                <i className="fas fa-receipt"></i>
                Détails de la commande
              </h2>
              <p>{formatDate(selectedOrder.createdAt)}</p>
            </div>

            <div style={{ padding: '0 2rem 2rem' }}>
              <div className="order-product-info">
                <h3>Informations client</h3>
                <p><strong>Nom :</strong> {selectedOrder.customerName}</p>
                <p><strong>Téléphone :</strong> {selectedOrder.phone}</p>
                <p><strong>Adresse :</strong> {selectedOrder.address}</p>
                <p><strong>Code promo :</strong> {selectedOrder.promoCode || 'Aucun'}</p>
              </div>

              <h3 style={{ marginTop: '1.5rem' }}>Produits commandés</h3>
              <div className="table-responsive">
                <table className="products-table">
                  <thead>
                    <tr>
                      <th>Produit</th>
                      <th>Qté</th>
                      <th>Prix unitaire</th>
                      <th>Sous-total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedOrder.products || []).map((item, index) => (
                      <tr key={index}>
                        <td>
                          <strong>{item.name}</strong>
                          {item.shortTitle && <><br /><small>{item.shortTitle}</small></>}
                        </td>
                        <td>{item.quantity}</td>
                        <td>{formatPrice(item.price)}</td>
                        <td>{formatPrice(item.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="order-price" style={{ marginTop: '1rem', textAlign: 'right' }}>
                Total : {formatPrice(selectedOrder.totalPrice)}
              </div>

              <div style={{ marginTop: '1.5rem', textAlign: 'right' }}>
                <button
                  className="btn btn-danger"
                  onClick={() => handleDeleteOrder(selectedOrder)}
                >
                  <i className="fas fa-trash"></i> Supprimer cette commande
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
